import type React from 'react';

// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../hooks/tenants/useTenantFeatures';
import { usePremiumSubscription } from '../../hooks/premium';
import TenantFeatureRoute from './TenantFeatureRoute';

export interface PremiumFeatureRouteProps {
  children: React.ReactNode;
}

/**
 * `/premium` with the tenant's premium switch.
 *
 * Switched off, the page still belongs to an account that keeps catalog access
 * through its subscription (`access` of `GET payments/premium/subscription`,
 * the verdict the server's catalog gate uses): that account redirects only
 * when the switch is off AND `access` is false. So this waits for BOTH the
 * tenant and, only when the switch is off, the subscription answer before it
 * decides; with the switch on there is nothing to resolve and nothing is asked.
 *
 * A failed read counts as "no access": the server would refuse the catalog
 * without a proven entitlement, and the page without access has nothing left
 * for the account (the plans route answers 403 too).
 *
 * Platform users pass through `useTenantFeatures` (the switch always answers on
 * for them), without a subscription request.
 */
const PremiumFeatureRoute = ({ children }: PremiumFeatureRouteProps) => {
  const { isLoading, isOn } = useTenantFeatures();
  const needsEntitlement = !isLoading && !isOn('premium');
  const { data: status, isLoading: isEntitlementLoading } =
    usePremiumSubscription({ enabled: needsEntitlement, silent: true });

  if (isLoading || (needsEntitlement && isEntitlementLoading)) return null;

  return (
    <TenantFeatureRoute allow={Boolean(status?.access)} feature="premium">
      {children}
    </TenantFeatureRoute>
  );
};

export default PremiumFeatureRoute;
